import React, { useState } from 'react';
import { Box, Typography, Container, Paper, Stepper, Step, StepLabel, Button, CircularProgress, Alert } from '@mui/material';
import CsvFileUploader from '../components/UserUploader/CsvFileUploader';
import { useOrganization } from '../contexts/OrganizationContext';
import { api } from '../api/client/axiosClient';
import { ENDPOINTS } from '../api/endpoints';
import { useSnackbar } from 'notistack';

interface CsvData {
  headers: string[];
  data: Record<string, string>[];
}

interface LogoUpdateResult {
  organizationId: string;
  url: string;
  success: boolean;
  error?: string;
}

// Define step names for the Stepper
const steps = [
  'Upload CSV',
  'Preview Data',
  'Process & Report',
];

const LogoUploaderPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [csvData, setCsvData] = useState<CsvData | null>(null);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<LogoUpdateResult[]>([]);
  const [csvParsingError, setCsvParsingError] = useState<string | null>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);

  const _orgData = useOrganization();
  const { enqueueSnackbar } = useSnackbar();

  const handleNext = () => {
    setCsvParsingError(null);
    setProcessingError(null);
    
    if (activeStep === steps.indexOf('Upload CSV')) {
      if (!csvData) {
        setCsvParsingError('Please upload a CSV file first.');
        return;
      }
      setActiveStep(steps.indexOf('Preview Data'));
    } else if (activeStep === steps.indexOf('Preview Data')) {
      handleProcessLogos();
    }
  };

  const handleBack = () => {
    setCsvParsingError(null);
    setProcessingError(null);
    if (activeStep === steps.indexOf('Process & Report')) {
      setResults([]);
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCsvParsed = (data: CsvData) => {
    // Validate CSV structure
    if (!data.headers.includes('organizationId') || !data.headers.includes('url')) {
      setCsvParsingError('CSV must contain columns: organizationId, url');
      return;
    }
    
    setCsvData(data);
    setCsvParsingError(null);
    setResults([]);
  };

  const handleCsvParsingError = (error: string) => {
    setCsvParsingError(error);
    setCsvData(null);
    setResults([]);
  };

  const handleProcessLogos = async () => {
    if (!csvData) {
      setProcessingError('No CSV data to process.');
      return;
    }

    setProcessing(true);
    setProcessingError(null);
    setResults([]);

    const processResults: LogoUpdateResult[] = [];

    try {
      // Process each row
      for (const row of csvData.data) {
        const organizationId = row.organizationId?.trim();
        const logoUrl = row.url?.trim();

        if (!organizationId || !logoUrl) {
          processResults.push({
            organizationId: organizationId || 'N/A',
            url: logoUrl || 'N/A',
            success: false,
            error: 'Missing organizationId or url'
          });
          continue;
        }

        try {
          // Create FormData for the update
          const formData = new FormData();
          formData.append('organization', JSON.stringify({ logo: logoUrl }));

          // Call the update endpoint
          await api.put(`${ENDPOINTS.organizations.base()}/${organizationId}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          processResults.push({
            organizationId,
            url: logoUrl,
            success: true
          });

        } catch (error: any) {
          processResults.push({
            organizationId,
            url: logoUrl,
            success: false,
            error: error.response?.data?.message || error.message || 'Unknown error'
          });
        }
      }

      setResults(processResults);
      setActiveStep(steps.indexOf('Process & Report'));

      // Show summary
      const successCount = processResults.filter(r => r.success).length;
      const errorCount = processResults.filter(r => !r.success).length;
      
      enqueueSnackbar(
        `Procesamiento completado: ${successCount} exitosos, ${errorCount} con errores`,
        { variant: successCount > 0 ? 'success' : 'warning' }
      );

    } catch (error: any) {
      setProcessingError(error.message || 'Error during processing');
    } finally {
      setProcessing(false);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0: // Upload CSV
        return (
          <Box>
            {csvParsingError && <Alert severity="error" sx={{ mb: 2 }}>{csvParsingError}</Alert>}
            <Typography variant="h6" gutterBottom>
              Subir CSV de Logos
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              El CSV debe contener las siguientes columnas:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li><strong>organizationId</strong>: ID de la organización a actualizar</li>
              <li><strong>url</strong>: URL del nuevo logo</li>
            </Box>
            <CsvFileUploader onCsvParsed={handleCsvParsed} onParsingError={handleCsvParsingError} />
          </Box>
        );
      
      case 1: // Preview Data
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Vista Previa de Datos
            </Typography>
            {csvData && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Se procesarán <strong>{csvData.data.length}</strong> organizaciones
                </Typography>
                
                {/* Estadísticas básicas */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 3, 
                  mb: 3,
                  p: 2,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 1,
                  border: '1px solid #e9ecef'
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {csvData.data.length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Organizaciones
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {csvData.data.filter(row => row.organizationId?.trim() && row.url?.trim()).length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Válidas
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="error.main">
                      {csvData.data.filter(row => !row.organizationId?.trim() || !row.url?.trim()).length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Con Errores
                    </Typography>
                  </Box>
                </Box>

                {/* Muestra solo las primeras 3 filas como ejemplo */}
                <Typography variant="subtitle2" gutterBottom>
                  Muestra de datos (primeras 3 filas):
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '8px', border: '1px solid #ddd' }}>Organization ID</th>
                        <th style={{ padding: '8px', border: '1px solid #ddd' }}>URL del Logo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.data.slice(0, 3).map((row, index) => (
                        <tr key={index}>
                          <td style={{ padding: '8px', border: '1px solid #ddd' }}>{row.organizationId}</td>
                          <td style={{ padding: '8px', border: '1px solid #ddd', wordBreak: 'break-all' }}>{row.url}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
                
                {csvData.data.length > 3 && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    ... y {csvData.data.length - 3} filas más
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      
      case 2: { // Process & Report
        if (processing) {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Typography>Procesando logos...</Typography>
            </Box>
          );
        }
        
        if (processingError) {
          return <Alert severity="error">{processingError}</Alert>;
        }
        
        const successCount = results.filter(r => r.success).length;
        const errorCount = results.filter(r => !r.success).length;
        const successRate = results.length > 0 ? ((successCount / results.length) * 100).toFixed(1) : 0;

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Reporte de Procesamiento
            </Typography>
            
            {/* Estadísticas principales */}
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              mb: 3,
              p: 3,
              backgroundColor: '#f8f9fa',
              borderRadius: 2,
              border: '1px solid #e9ecef'
            }}>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h3" color="primary">
                  {results.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Procesados
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h3" color="success.main">
                  {successCount}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Exitosos
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h3" color="error.main">
                  {errorCount}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Con Errores
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h3" color={Number(successRate) >= 80 ? 'success.main' : Number(successRate) >= 60 ? 'warning.main' : 'error.main'}>
                  {successRate}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Tasa de Éxito
                </Typography>
              </Box>
            </Box>

            {/* Resumen de errores más comunes */}
            {errorCount > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Resumen de Errores:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7',
                  borderRadius: 1
                }}>
                  {(() => {
                    const errorSummary: Record<string, number> = {};
                    results.filter(r => !r.success).forEach(result => {
                      const error = result.error || 'Error desconocido';
                      errorSummary[error] = (errorSummary[error] || 0) + 1;
                    });
                    
                    return Object.entries(errorSummary)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5) // Mostrar solo los 5 errores más comunes
                      .map(([error, count]) => (
                        <Typography key={error} variant="body2" sx={{ mb: 0.5 }}>
                          • {error}: {count} ocurrencias
                        </Typography>
                      ));
                  })()}
                </Box>
              </Box>
            )}

            {/* Botón para descargar reporte detallado */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  const csvContent = [
                    'Organization ID,URL,Estado,Error',
                    ...results.map(r => [
                      r.organizationId,
                      r.url,
                      r.success ? 'Exitoso' : 'Error',
                      r.error || ''
                    ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
                  ].join('\n');
                  
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.setAttribute('href', url);
                  link.setAttribute('download', `logo-upload-report-${new Date().toISOString().split('T')[0]}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }}
                sx={{ mr: 2 }}
              >
                Descargar Reporte Detallado (CSV)
              </Button>
            </Box>
          </Box>
        );
      }

      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ my: 4, p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Logo Uploader
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 2, mb: 2, p: 2, border: '1px dashed grey', minHeight: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'start' }}>
          {getStepContent(activeStep)}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0 || processing}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          {activeStep !== steps.indexOf('Process & Report') && (
            <Button 
              variant="contained"
              onClick={handleNext} 
              disabled={processing || (activeStep === 0 && !csvData)}
            >
              {processing && <CircularProgress size={24} sx={{ mr: 1 }} />}
              {activeStep === steps.indexOf('Upload CSV') ? 'Next' : 'Process Logos'}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default LogoUploaderPage;
